<script lang="ts" setup>
import {computed, h, type PropType, watch} from 'vue'
import {NButton, NDropdown} from 'naive-ui'
import {RouterLink, useRoute, useRouter} from 'vue-router'
import {useAuthStore} from "@/stores/authentication-store";
import { Person20Regular, Flag20Regular, Branch20Regular, SignOut20Regular, Navigation20Regular, Dismiss20Regular, Settings20Regular } from '@vicons/fluent';
import {renderIcon} from "@/helpers/render-helpers";
import {useThemeStore} from "@/stores/theme-store";
import {useNavigationStore} from "@/stores/navigation-store";
import {useI18n} from "vue-i18n";
import {ScreenSize} from "@/constants/screen-size-helper";
import OrganizationSwitch from "@/components/layout/OrganizationSwitch.vue";
import {RoleOptions} from '@/constants/role';
import {getFromOptions} from '@/helpers/select-options-helper';
import type {DropdownMixedOption} from 'naive-ui/es/dropdown/src/interface';
import LanguageSwitcher from "@/components/LanguageSwitcher.vue";
import packageJson from "../../package.json";
import type {MenuOption} from "@/models/menu/menu-option";

const {t} = useI18n();

const cssVar = (val: string) => {
    return getComputedStyle(document.body).getPropertyValue(val);
}

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const themeStore = useThemeStore();
const navigationStore = useNavigationStore();

const props = defineProps({
  menuOptions: {
    type: Array as PropType<MenuOption[]>,
    required: true,
  }
})

//User options (in user dropdown, and in mobile menu)
const userOptions: DropdownMixedOption[] = [
  {
    label: () => {
      return t(getFromOptions(RoleOptions, authStore.role)!)
    },
    key: 'role',
    icon: renderIcon(Person20Regular),
  },
  {
    label: () => t("page_titles.organization.title"),
    key: 'language',
    type: 'render',
    render: () => {
      return h(LanguageSwitcher, {})
    },
    icon: renderIcon(Flag20Regular),
  },
  {
    label: () => {
      return `${t("core.version")} ${packageJson.version}`;
    },
    key: 'version',
    icon: renderIcon(Branch20Regular),
  },
  {
    label: () => t("profile.title"),
    key: 'profile',
    icon: renderIcon(Settings20Regular),
    props: {
      onClick: () => {
        router.push({ name: 'profile' });
      }
    },
  },
  {
    label: () => t("core.signout"),
    key: 'signout',
    props: {
      onClick: () => {
        authStore.logout();
      }
    },
    icon: renderIcon(SignOut20Regular),
  },
]

watch(() => route.path, () => {
  navigationStore.mobileMenu.visible = false;
})

const renderBurger = computed(() => {
  return !navigationStore.mobileMenu.visible ? renderIcon(Navigation20Regular) : renderIcon(Dismiss20Regular);
})

</script>
<template>
  <header>
    <router-link to="/">
      <figure class="logo">
        <img src="@/assets/logo-white.png" alt="Districar logo"/>
      </figure>
    </router-link>

    <nav class="header-nav">
      <ul>
        <template v-for="menuOption in props.menuOptions">
          <li v-if="menuOption.show" :key="menuOption.key">
            <router-link :to="menuOption.to" :data-text="menuOption.label">
              {{ menuOption.label }}
            </router-link>
          </li>
        </template>
      </ul>
    </nav>

    <div class="header-right">
      <organization-switch v-if="themeStore.screenSize == ScreenSize.Default"/>

      <n-dropdown v-if="themeStore.screenSize == ScreenSize.Default" :options="userOptions" trigger="click">
        <n-button :render-icon="renderIcon(Person20Regular)" text class="dropdown-button">
          {{ authStore.username }}
        </n-button>
      </n-dropdown>

      <n-button v-if="themeStore.screenSize != ScreenSize.Default" :render-icon="renderBurger" quaternary round :color="cssVar('--dis-color-icon-inverted')" size="small"
                @click="navigationStore.openMobileMenu()">
      </n-button>
    </div>
  </header>
</template>

<style lang="scss" scoped>
header {
  height: var(--header-height);
  padding: 0 var(--dis-space-4_0x);
  background: linear-gradient(90deg, var(--dis-color-other-gradient_from_left) 0%, var(--dis-color-other-gradient_to_right) 100%);
  display: flex;
  align-items: center;
}

.logo {
  height: 20px;
  width: 120px;
  margin: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: left center;
  }
}

.header-right {
  margin-left: auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--dis-space-2_0x);
}

.dropdown-button {
  height: var(--header-height);
  padding: 0 var(--dis-space-1_0x);
  color: var(--dis-color-primary-200);
  font-weight: var(--dis-font-weight-regular) !important;
  &:hover, &:active, &:focus {
    color: color-mix(in srgb, var(--dis-color-neutral-white) 100%, transparent);
  }
}
</style>
